import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export type Landmark = { x: number; y: number; z?: number };

export interface MyDB extends DBSchema {
  landmarks: {
    key: number;
    value: {
      id?: number;
      formDataId: number;
      mediaIndex: number;
      landmarks: Landmark[];
      timestamp: number;
    };
    indexes: { 'by-formDataId': number };
  };
  videos: {
    key: string;
    value: {
      clientRunId: string;
      blob: Blob;
      mimeType: string | null;
      bytes: number;
      updatedAt: number;
    };
  };
}

const DB_NAME = 'my-db';
const DB_VERSION = 6;

export async function openOrCreateDB(): Promise<IDBPDatabase<MyDB>> {
  const db = await openDB<MyDB>(DB_NAME, DB_VERSION, {
    upgrade(db, _oldVersion, _newVersion, transaction) {
      let landmarkStore: IDBObjectStore;
      if (!db.objectStoreNames.contains('landmarks')) {
        landmarkStore = db.createObjectStore('landmarks', {
          keyPath: 'id',
          autoIncrement: true,
        });
      } else {
        landmarkStore = transaction.objectStore('landmarks');
      }
      if (!landmarkStore.indexNames.contains('by-formDataId')) {
        landmarkStore.createIndex('by-formDataId', 'formDataId');
      }
      if (!db.objectStoreNames.contains('videos')) {
        db.createObjectStore('videos', {
          keyPath: 'clientRunId',
        });
      }
    },
  });
  if (!db.objectStoreNames.contains('landmarks')) {
    throw new Error("Missing 'landmarks' store after openDB/upgrade!");
  }
  if (!db.objectStoreNames.contains('videos')) {
    throw new Error("Missing 'videos' store after openDB/upgrade!");
  }
  return db;
}

export async function saveVideoArtifact(
  clientRunId: string,
  blob: Blob,
  mimeType: string | null,
): Promise<void> {
  if (!clientRunId || !blob || blob.size <= 0) return;
  const db = await openOrCreateDB();
  try {
    await db.put('videos', {
      clientRunId,
      blob,
      mimeType,
      bytes: blob.size,
      updatedAt: Date.now(),
    });
  } finally {
    db.close();
  }
}

export async function readVideoArtifact(
  clientRunId: string,
): Promise<MyDB['videos']['value'] | null> {
  if (!clientRunId) return null;
  const db = await openOrCreateDB();
  try {
    return (await db.get('videos', clientRunId)) ?? null;
  } finally {
    db.close();
  }
}

export async function deleteVideoArtifact(clientRunId: string): Promise<void> {
  if (!clientRunId) return;
  const db = await openOrCreateDB();
  try {
    await db.delete('videos', clientRunId);
  } finally {
    db.close();
  }
}
